const MAX_LENGTHS = {
  project: 50,
  richDescription: 1000,
  assistanceReason: 200,
};

const ASSISTANCE_TRUE_VALUES = new Set(["1", "y", "yes", "true"]);
const ASSISTANCE_FALSE_VALUES = new Set(["0", "n", "no", "false", ""]);
const PREFILL_QUERY_PARAMS = [
  "users",
  "projectIds",
  "project",
  "assistance",
  "assistant",
  "assistanceReason",
  "material",
  "originId",
  "description",
  "richDescription",
];

const trimToLength = (value, maxLength) => {
  if (typeof value !== "string") return "";
  const trimmed = value.trim();
  return trimmed.length > maxLength ? trimmed.slice(0, maxLength) : trimmed;
};

const parseCsvParam = (rawValue) => {
  if (typeof rawValue !== "string") return [];

  const seen = new Set();
  return rawValue
    .split(",")
    .map((item) => item.trim())
    .filter((item) => item.length > 0)
    .filter((item) => {
      const dedupeKey = item.toLowerCase();
      if (seen.has(dedupeKey)) return false;
      seen.add(dedupeKey);
      return true;
    });
};

const normalizeAssistance = (rawValue) => {
  if (rawValue == null) return false;

  const normalized = String(rawValue).trim().toLowerCase();
  if (ASSISTANCE_TRUE_VALUES.has(normalized)) return true;
  if (ASSISTANCE_FALSE_VALUES.has(normalized)) return false;

  return false;
};

const toSearchParams = (input) => {
  if (input instanceof URLSearchParams) {
    return input;
  }

  if (typeof input === "string") {
    const queryString = input.startsWith("?") ? input.slice(1) : input;
    return new URLSearchParams(queryString);
  }

  return new URLSearchParams();
};

const normalizeIdentity = (value) => String(value || "").trim().toLowerCase();

// Prefill identities may arrive with a site/environment prefix (e.g.
// "wamqa.roche.com_senkod"). We only ever want to match on the plain
// username after the last underscore, never on the raw prefixed value or
// the userId itself.
const getUsernameFromIdentity = (rawIdentity) => {
  const normalized = normalizeIdentity(rawIdentity);
  if (!normalized) return "";

  const underscoreIndex = normalized.lastIndexOf("_");
  if (underscoreIndex > -1 && underscoreIndex < normalized.length - 1) {
    return normalized.slice(underscoreIndex + 1);
  }

  return normalized;
};

const getIdentityCandidates = (rawIdentity) => {
  const username = getUsernameFromIdentity(rawIdentity);
  return username ? [username] : [];
};

const getIdentityKeysForUser = (user = {}) => {
  const email = typeof user?.email === "string" ? user.email.trim() : "";
  const emailLocalPart = email.includes("@") ? email.split("@")[0] : "";

  return [
    typeof user?.userId === "string" ? user.userId.trim() : "",
    typeof user?.user === "string" ? user.user.trim() : "",
    typeof user?.name === "string" ? user.name.trim() : "",
    email,
    emailLocalPart,
  ]
    .filter(Boolean)
    .map(normalizeIdentity);
};

export const findUserByPrefillIdentity = (usersDirectory = [], rawIdentity) => {
  const identityCandidates = getIdentityCandidates(rawIdentity);
  if (identityCandidates.length === 0) return null;

  return (
    usersDirectory.find((user) => {
      const keys = getIdentityKeysForUser(user);
      return identityCandidates.some((candidate) => {
        if (keys.includes(candidate)) return true;

        // Allow matching username suffixes against email local-part keys.
        if (candidate.length >= 4 && !candidate.includes("@")) {
          return keys.some((key) => key.endsWith(`.${candidate}`));
        }

        return false;
      });
    }) || null
  );
};

export const PREFILL_ERROR_CODES = {
  MISSING_USERS: "MISSING_USERS",
  MISSING_PROJECT: "MISSING_PROJECT",
  MISSING_ORIGIN_ID: "MISSING_ORIGIN_ID",
};

const PREFILL_ERROR_MESSAGES = {
  [PREFILL_ERROR_CODES.MISSING_USERS]: "missing booking users",
  [PREFILL_ERROR_CODES.MISSING_PROJECT]: "missing project",
  [PREFILL_ERROR_CODES.MISSING_ORIGIN_ID]: "missing origin id",
};

export const buildPrefillInvalidMessage = (errors = []) => {
  const details = errors.map((code) => PREFILL_ERROR_MESSAGES[code] || code).filter(Boolean);
  const suffix = details.length > 0 ? `: ${details.join(", ")}` : "";
  return `URL prefill is invalid or incomplete${suffix}. Please check the link and try again.`;
};

export const buildPrefillWarningMessage = (warnings = {}) => {
  const parts = [];
  if (warnings.unknownUsers?.length > 0) {
    parts.push(`unknown user(s): ${warnings.unknownUsers.join(", ")}`);
  }
  if (warnings.unknownAssistant) {
    parts.push(`unknown assistant: ${warnings.unknownAssistant}`);
  }
  return `Some prefilled identities could not be found and were skipped (${parts.join("; ")}).`;
};

export const parseGroupViewPrefill = (queryInput) => {
  const params = toSearchParams(queryInput);

  const users = parseCsvParam(params.get("users"));
  const projectIds = parseCsvParam(params.get("projectIds"));
  const project = trimToLength(params.get("project"), MAX_LENGTHS.project);
  const assistance = normalizeAssistance(params.get("assistance"));
  const assistant = trimToLength(params.get("assistant"), 256);
  const assistanceReason = trimToLength(params.get("assistanceReason"), MAX_LENGTHS.assistanceReason);
  const material = trimToLength(params.get("material"), 1000);
  const originId = trimToLength(params.get("originId"), 256);
  const richDescriptionSource = params.get("richDescription");
  const legacyDescriptionSource = params.get("description");
  const richDescription = trimToLength(
    richDescriptionSource != null ? richDescriptionSource : legacyDescriptionSource,
    MAX_LENGTHS.richDescription
  );

  const errors = [];

  if (users.length === 0) {
    errors.push(PREFILL_ERROR_CODES.MISSING_USERS);
  }

  if (projectIds.length === 0 && project.length === 0) {
    errors.push(PREFILL_ERROR_CODES.MISSING_PROJECT);
  }

  if (originId.length === 0) {
    errors.push(PREFILL_ERROR_CODES.MISSING_ORIGIN_ID);
  }

  return {
    isValid: errors.length === 0,
    errors,
    normalizedPrefill: {
      users,
      projectIds,
      project,
      assistance,
      assistant: assistant || null,
      assistanceReason,
      material,
      originId,
      richDescription,
    },
  };
};

const getCanonicalUserId = (user) => {
  // `state.users.users` (from listDigitalLabBookingUsers) exposes the stable
  // login id as `userId`, not `user`. Prefer it so the resolved id actually
  // matches records elsewhere (assistant lookups, approval dialogs, backend
  // `assistent` field), instead of falling back to the display `name`.
  const byUserId = typeof user?.userId === "string" ? user.userId.trim() : "";
  if (byUserId) return byUserId;

  const byUser = typeof user?.user === "string" ? user.user.trim() : "";
  if (byUser) return byUser;

  const byName = typeof user?.name === "string" ? user.name.trim() : "";
  return byName;
};

export const resolveGroupViewPrefillIdentities = (prefill, usersDirectory = []) => {
  if (!prefill || typeof prefill !== "object") {
    return {
      normalizedPrefill: prefill,
      warnings: {
        unknownUsers: [],
        unknownAssistant: null,
      },
    };
  }

  if (!Array.isArray(usersDirectory) || usersDirectory.length === 0) {
    return {
      normalizedPrefill: prefill,
      warnings: {
        unknownUsers: [],
        unknownAssistant: null,
      },
    };
  }

  const resolvedUsers = [];
  const unknownUsers = [];
  const seenUsers = new Set();

  (prefill.users || []).forEach((rawUserId) => {
    const resolvedUser = findUserByPrefillIdentity(usersDirectory, rawUserId);
    const resolvedUserId = getCanonicalUserId(resolvedUser);
    const fallbackUserId = String(rawUserId || "").trim();

    if (!resolvedUserId) {
      if (fallbackUserId) unknownUsers.push(rawUserId);
      return;
    }

    const selectedUserId = resolvedUserId;

    const dedupeKey = selectedUserId.toLowerCase();
    if (seenUsers.has(dedupeKey)) return;
    seenUsers.add(dedupeKey);
    resolvedUsers.push(selectedUserId);
  });

  let resolvedAssistant = null;
  let unknownAssistant = null;
  if (prefill.assistant) {
    const resolvedAssistantUser = findUserByPrefillIdentity(usersDirectory, prefill.assistant);
    const resolvedAssistantUserId = getCanonicalUserId(resolvedAssistantUser);
    if (resolvedAssistantUserId) {
      resolvedAssistant = resolvedAssistantUserId;
    } else {
      unknownAssistant = prefill.assistant;
      resolvedAssistant = null;
    }
  }

  const normalizedAssistance = Boolean(prefill.assistance) && Boolean(resolvedAssistant);

  return {
    normalizedPrefill: {
      ...prefill,
      users: resolvedUsers,
      assistance: normalizedAssistance,
      assistant: resolvedAssistant,
    },
    warnings: {
      unknownUsers,
      unknownAssistant,
    },
  };
};

export const groupViewPrefillLimits = { ...MAX_LENGTHS };

export const hasGroupViewPrefillParams = (queryInput) => {
  const params = toSearchParams(queryInput);
  return PREFILL_QUERY_PARAMS.some((key) => params.has(key));
};
